import React, { useEffect, useState } from 'react';
import { Megaphone, Loader2, Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ADMIN_NAV } from '../../constants/nav';
import { adminListAds, adminCreateAd, adminUpdateAd, adminDeleteAd, AdPayload } from '../../features/ads/api/ads';
import type { Ad } from '../../types';
import { ADS } from '../../constants/testIds';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { DateTimeField } from '../../components/common/DatePickerFields';

const inputClass = 'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';

const emptyAd: AdPayload = {
  title: '', content: '', type: 'topbar', image_url: '', button_label: '', button_link: '',
  target_audience: 'all', start_at: '', end_at: '', is_active: true, priority: 0, send_email: false,
};

const badge = (t: string, cls: string) => <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${cls}`}>{t}</span>;

// ISO <-> datetime-local helpers
const isoToLocalInput = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localInputToIso = (val: string): string => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toISOString();
};

export default function AdminAdsPage() {
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState<Ad | null>(null);
  const [form, setForm] = useState<AdPayload>(emptyAd);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); adminListAds().then(setAds).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(emptyAd); setDialog(true); };
  const openEdit = (a: Ad) => {
    setEditing(a);
    setForm({
      title: a.title, content: a.content, type: a.type, image_url: a.image_url,
      button_label: a.button_label, button_link: a.button_link, target_audience: a.target_audience,
      start_at: a.start_at, end_at: a.end_at, is_active: a.is_active, priority: a.priority,
    });
    setDialog(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      if (editing) await adminUpdateAd(editing.id, form); else await adminCreateAd(form);
      toast.success('Iklan tersimpan'); setDialog(false); load();
    } catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };
  const del = async (a: Ad) => { if (!window.confirm(`Hapus iklan "${a.title}"?`)) return; try { await adminDeleteAd(a.id); toast.success('Iklan dihapus'); load(); } catch (e: any) { toast.error('Gagal', { description: e.message }); } };
  const toggle = async (a: Ad) => { try { await adminUpdateAd(a.id, { is_active: !a.is_active }); load(); } catch (e: any) { toast.error('Gagal', { description: e.message }); } };

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Ads / Pengumuman">
      <div className="max-w-4xl" data-testid={ADS.adminPage}>
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center"><Megaphone className="w-5 h-5 text-cyan-400" strokeWidth={1.5} /></div>
            <div><p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Marketing</p><h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Ads & Pengumuman</h2></div>
          </div>
          <button data-testid={ADS.newBtn} onClick={openNew} className="bg-cyan-400 text-[#050914] font-medium px-4 py-2 rounded-lg hover:bg-cyan-300 transition-colors text-sm flex items-center gap-1.5"><Plus className="w-4 h-4" strokeWidth={2} /> Iklan Baru</button>
        </div>

        {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div> : (
          <div className="space-y-2">
            {ads.length === 0 && <p className="text-white/40 text-sm py-10 text-center">Belum ada iklan. Buat yang pertama.</p>}
            {ads.map((a) => (
              <div key={a.id} data-testid={ADS.row} className="flex items-center gap-3 bg-white/[0.02] border border-white/10 rounded-xl p-4">
                <span className="text-white/40 text-xs w-6" title="priority">{a.priority}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white text-sm font-medium truncate">{a.title}</p>
                    {badge(a.type, a.type === 'popup' ? 'text-purple-300 bg-purple-400/10 border-purple-400/20' : 'text-cyan-300 bg-cyan-400/10 border-cyan-400/20')}
                    {badge(a.target_audience, 'text-amber-300 bg-amber-400/10 border-amber-400/20')}
                    {!a.is_active && badge('nonaktif', 'text-white/50 bg-white/5 border-white/10')}
                  </div>
                  {a.content && <p className="text-xs text-white/50 truncate mt-0.5">{a.content}</p>}
                </div>
                <button data-testid={ADS.toggleBtn} onClick={() => toggle(a)} className="p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5">{a.is_active ? <Eye className="w-4 h-4" strokeWidth={1.5} /> : <EyeOff className="w-4 h-4" strokeWidth={1.5} />}</button>
                <button data-testid="admin-ad-edit" onClick={() => openEdit(a)} className="p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                <button data-testid={ADS.deleteBtn} onClick={() => del(a)} className="p-2 rounded-lg text-white/60 hover:text-red-400 hover:bg-white/5"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Iklan' : 'Iklan Baru'}</DialogTitle><DialogDescription className="text-white/50">Topbar tampil sebagai banner atas, popup tampil sebagai dialog.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><label className={labelClass}>Judul</label><input data-testid="ad-form-title" className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><label className={labelClass}>Konten</label><textarea className={inputClass} rows={2} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={labelClass}>Tipe</label><select data-testid="ad-form-type" className={inputClass} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}><option value="topbar">Topbar</option><option value="popup">Popup</option></select></div>
              <div><label className={labelClass}>Target</label><select data-testid="ad-form-audience" className={inputClass} value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })}><option value="all">All</option><option value="member">Member (Non-VIP)</option><option value="vip">VIP</option></select></div>
              <div><label className={labelClass}>Prioritas</label><input type="number" className={inputClass} value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelClass}>Label Tombol</label><input className={inputClass} value={form.button_label} onChange={(e) => setForm({ ...form, button_label: e.target.value })} placeholder="Lihat Course" /></div>
              <div><label className={labelClass}>Link Tombol</label><input className={inputClass} value={form.button_link} onChange={(e) => setForm({ ...form, button_link: e.target.value })} placeholder="/courses" /></div>
            </div>
            <div><label className={labelClass}>Image URL (popup)</label><input className={inputClass} value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelClass}>Mulai (opsional)</label><DateTimeField withTime testId="ad-form-start" value={isoToLocalInput(form.start_at || '')} onChange={(v) => setForm({ ...form, start_at: localInputToIso(v) })} /></div>
              <div><label className={labelClass}>Selesai (opsional)</label><DateTimeField withTime testId="ad-form-end" value={isoToLocalInput(form.end_at || '')} onChange={(v) => setForm({ ...form, end_at: localInputToIso(v) })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-cyan-400 w-4 h-4" /> Aktif</label>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer" data-testid="ad-send-email"><input type="checkbox" checked={!!form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} className="accent-cyan-400 w-4 h-4" /> Kirim juga ke email member (saat dibuat)</label>
          </div>
          <DialogFooter>
            <button data-testid={ADS.saveBtn} onClick={save} disabled={saving || !form.title} className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">{saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />} Simpan</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
